import React, { useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    IoArrowBackCircle,
    IoCheckmarkCircleOutline,
    IoCloseCircleOutline,
    IoMailOutline,
    IoDocumentTextOutline,
    IoInformationCircle,
    IoListOutline,
    IoFolderOpenOutline,
    IoChevronDownOutline,
    IoChevronForwardOutline,
    IoCalendarOutline,
    IoPersonOutline,
    IoTimeOutline,
    IoCheckmarkCircle,
    IoCloseCircle,
    IoAddCircleOutline,
    IoOpenOutline,
    IoSendOutline,
    IoKeyOutline,
} from "react-icons/io5";
import { FaCommentDots, FaUser } from "react-icons/fa";
import { MdBlock } from "react-icons/md";
import { columnDescriptions } from "../columnDescriptions";
import { Loading } from "../../shared/loading/Loading";
import { Alert } from "../../shared/alert/Alert";
import { EmailModal } from "../../shared/modal/EmailModal";
import { useAuth } from "../../../contexts/AuthContext";
import { useFornecedores, useDocumentos } from "../../../hooks/useFornecedores";
import { GrIntegration } from "react-icons/gr";
import Style from "./index.module.css";

const STATUS_MAP = {
    ativo: { label: "Ativo", color: "#28a745", icon: <IoCheckmarkCircleOutline /> },
    inativo: { label: "Inativo", color: "#6c757d", icon: <IoCloseCircleOutline /> },
    pendente: { label: "Pendente", color: "#ffc107", icon: <IoTimeOutline /> },
    aprovado: { label: "Aprovado", color: "#28a745", icon: <IoCheckmarkCircle /> },
    reprovado: { label: "Reprovado", color: "#dc3545", icon: <IoCloseCircle /> },
    respondido: { label: "Respondido", color: "#007bff", icon: <IoCheckmarkCircle /> },
};

const FIELD_GROUPS = {
    dadosBasicos: ["codcli", "cgccli", "tipcli", "nomcli", "fancli", "insest", "tipserv", "descserv", "datcadinipf", "datcadfinpf"],
    endereco: ["cepcli", "estcid", "codcid", "nomcid", "baicli", "endcli"],
    contatos: ["endele", "endalt", "concli", "foncla", "fonclb", "celcli", "siteurlcli", "redesoccli"],
    status: ["cliafa", "cadinipfaprov", "cadfinpfaprov"]
};

export const FornecedorVisualizacao = () => {
    const { id } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();

    const [open, setOpen] = useState(false);
    const [openSubAfastar, setOpenSubAfastar] = useState(false);
    const [activeTab, setActiveTab] = useState("dados");
    const [expandedRegistro, setExpandedRegistro] = useState(null);
    const [activeRegistroTab, setActiveRegistroTab] = useState(
        user?.tipusu === "FORNECEDOR" ? "questionario" : "avaliacao"
    );
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [emailData, setEmailData] = useState({
        destinatario: '',
        assunto: '',
        conteudo: ''
    });

    const {
        fornecedor,
        consultaSintegra,
        avaliacoes,
        isLoadingFornecedor,
        isLoadingAvaliacoes,
        isLoadingAction,
        gerarSenhaTemporaria,
        ativarFornecedor,
        inativarFornecedor,
        novaAvaliacao,
        aprovarAvaliacao,
        reprovarAvaliacao,
        enviarEmail,
    } = useFornecedores(user, id);

    const toggleDropdown = useCallback(() => setOpen(prev => !prev), []);
    const toggleRegistroExpansion = useCallback((registroId) => {
        if (expandedRegistro !== registroId) {
            setActiveRegistroTab(user?.tipusu === "FORNECEDOR" ? "questionario" : "avaliacao");
        }
        setExpandedRegistro(prev => prev === registroId ? null : registroId);
    }, [expandedRegistro]);

    const handleRegistroTabChange = useCallback((tab) => {
        setActiveRegistroTab(tab);
    }, []);

    const handleEnviarEmailClick = useCallback(() => {
        setShowEmailModal(true);
        setOpen(false);
    }, []);

    const handleEnviarEmail = useCallback(async () => {
        if (!emailData.assunto.trim() || !emailData.conteudo.trim()) {
            await Alert({
                icon: "warning",
                title: "Atenção",
                message: "Por favor, preencha o assunto e o conteúdo do email.",
                showConfirmButton: true,
                confirmButtonText: "OK"
            });
            return;
        }

        enviarEmail(emailData.destinatario, emailData.assunto, emailData.conteudo);
        setShowEmailModal(false);
        setEmailData({ destinatario: '', assunto: '', conteudo: '' });
    }, [emailData, enviarEmail]);

    const calcularTotaisQualidade = useCallback((questionario) => {
        if (!questionario || !Array.isArray(questionario)) return { total: 0, percentual: 0, status: 'N/A' };

        const grupoQualidade = questionario.find(grupo =>
            grupo.titulo === "Controle e Gestão de Qualidade"
        );

        if (!grupoQualidade || !grupoQualidade.perguntas) {
            return { total: 0, percentual: 0, status: 'N/A' };
        }

        const campoMap = {
            'Sistema de Qualidade': 'sisqua',
            'Análise de Soluções': 'anasol',
            'Instruções de Processo': 'inspro',
            'Regras': 'regras',
            'Certificação Ambiental': 'cercal',
            'Instrução de Duração': 'insdur',
            'Controles': 'contro',
            'Certificação de Qualidade': 'cerqua',
            'Liderança de Projeto': 'lidpro'
        };

        const safeInt = (valor) => {
            if (!valor || valor === '-' || valor === 'N/A') return 0;
            const num = parseInt(valor, 10);
            return isNaN(num) ? 0 : num;
        };

        let total = 0;

        grupoQualidade.perguntas.forEach(pergunta => {
            const campo = campoMap[pergunta.pergunta];
            if (campo) {
                total += safeInt(pergunta.resposta);
            }
        });

        const percentual = total > 0 ? Math.round((total / 27) * 100) : 0;

        let status = 'Reprovado';
        if (percentual > 70) {
            status = 'Aprovado S/Res';
        } else if (percentual >= 40 && percentual <= 69) {
            status = 'Aprovado C/Res';
        }

        return {
            total,
            percentual,
            status
        };
    }, []);

    const mapearAvaliacoesDaAPI = useCallback((avaliacoes) => {
        if (!avaliacoes || !Array.isArray(avaliacoes)) return [];

        return avaliacoes.map((avaliacao) => {
            const ultimaAprovacao = avaliacao.aprovacoes?.[0];

            return {
                id: avaliacao.arq486,
                tipo: user.tipusu === 'COMPRADOR' ? "Avaliação de Fornecedor" : "Questionário",
                data: avaliacao.datcad,
                avaliador: `${avaliacao.codori} - ${avaliacao.nomori}` || "Sistema",
                acao: ultimaAprovacao?.statusapr === "1" ? "aprovado" :
                    ultimaAprovacao?.statusapr === "0" ? "reprovado" : "pendente",
                observacao: ultimaAprovacao?.obsapr,
                historico: avaliacao.aprovacoes?.map(aprov => ({
                    id: aprov.arq483,
                    data: aprov.datapr,
                    usuario: `${aprov.codori} - ${aprov.nomori}` || "Sistema",
                    acao: aprov.statusapr === "1" ? "aprovado" :
                        aprov.statusapr === "0" ? "reprovado" : "pendente",
                    observacao: aprov.obsapr,
                    tipoAprovacao: aprov.tipapr
                })) || [],
                status_questionario: avaliacao.status_questionario,
                questionario: [
                    {
                        titulo: "Requisitos Legais",
                        perguntas: [
                            { pergunta: "Alvará", resposta: avaliacao.questionario.alvara === "S" ? "Sim" : avaliacao.questionario.alvara === "N" ? "Não" : "N/A" },
                            { pergunta: "Licença", resposta: avaliacao.questionario.licenc === "S" ? "Sim" : avaliacao.questionario.licenc === "N" ? "Não" : "N/A" },
                            { pergunta: "Fornecedor Local", resposta: avaliacao.questionario.frnloc === "S" ? "Sim" : avaliacao.questionario.frnloc === "N" ? "Não" : "N/A" },
                            { pergunta: "Fornecedor Sustentável", resposta: avaliacao.questionario.frnsus === "S" ? "Sim" : avaliacao.questionario.frnsus === "N" ? "Não" : "N/A" },
                            { pergunta: "Impacto Positivo", resposta: avaliacao.questionario.imppos === "S" ? "Sim" : avaliacao.questionario.imppos === "N" ? "Não" : "N/A" },
                            { pergunta: "LGPD", resposta: avaliacao.questionario.prlgpd === "S" ? "Sim" : avaliacao.questionario.prlgpd === "N" ? "Não" : "N/A" },
                        ].filter(p => p.resposta?.trim())
                    },
                    {
                        titulo: "Controle e Gestão de Qualidade",
                        perguntas: [
                            { pergunta: "Sistema de Qualidade", resposta: avaliacao.questionario.sisqua || "N/A" },
                            { pergunta: "Análise de Soluções", resposta: avaliacao.questionario.anasol || "N/A" },
                            { pergunta: "Instruções de Processo", resposta: avaliacao.questionario.inspro || "N/A" },
                            { pergunta: "Regras", resposta: avaliacao.questionario.regras || "N/A" },
                            { pergunta: "Certificação Ambiental", resposta: avaliacao.questionario.cercal || "N/A" },
                            { pergunta: "Instrução de Duração", resposta: avaliacao.questionario.insdur || "N/A" },
                            { pergunta: "Controles", resposta: avaliacao.questionario.contro || "N/A" },
                            { pergunta: "Certificação de Qualidade", resposta: avaliacao.questionario.cerqua || "N/A" },
                            { pergunta: "Liderança de Projeto", resposta: avaliacao.questionario.lidpro || "N/A" },
                        ].filter(p => p.resposta?.trim())
                    },
                    {
                        titulo: "Clientes",
                        perguntas: [
                            { pergunta: "Cliente 1", resposta: `${avaliacao.questionario.nmcli1} - ${avaliacao.questionario.fncli1}` },
                            { pergunta: "Cliente 2", resposta: `${avaliacao.questionario.nmcli2} - ${avaliacao.questionario.fncli2}` },
                            { pergunta: "Cliente 3", resposta: `${avaliacao.questionario.nmcli3} - ${avaliacao.questionario.fncli3}` },
                        ].filter(p => p.resposta?.trim())
                    }
                ],
                aprovacoes: avaliacao.aprovacoes || []
            };
        });
    }, []);

    const renderStatus = useCallback((key) => {
        const value = fornecedor?.[key];
        const status = STATUS_MAP[value?.toLowerCase()] || { label: value || "-", color: "#000", icon: null };

        return (
            <span
                className={Style.badge}
                style={{
                    backgroundColor: `${status.color}25`,
                    color: status.color,
                    fontWeight: 'bold'
                }}
            >
                {status.icon && <span className={Style.badgeIcon}>{status.icon}</span>}
                {status.label}
            </span>
        );
    }, [fornecedor]);

    const renderCardGroup = useCallback((title, fields, isStatus = false) => (
        <div className={Style.cardGroup}>
            <h3>{title}</h3>
            <div className={Style.cardsContainer}>
                {fields.map((key) => (
                    <div key={key} className={Style.card}>
                        <span className={Style.cardLabel}>{columnDescriptions[key]}</span>
                        <span className={Style.cardValue}>
                            {isStatus ? renderStatus(key) : fornecedor?.[key] || "-"}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    ), [fornecedor, renderStatus]);

    const TotaisQualidade = React.memo(({ questionario }) => {
        const totais = calcularTotaisQualidade(questionario);

        return (
            <div className={Style.totaisContainer}>
                <h4>Resumo - Controle e Gestão de Qualidade</h4>
                <div className={Style.totaisGrid}>
                    <div className={Style.totalItem}>
                        <label>Pontuação Total:</label>
                        <span className={Style.totalValue}>{totais.total} / 27</span>
                    </div>
                    <div className={Style.totalItem}>
                        <label>Percentual:</label>
                        <span className={Style.totalValue}>{totais.percentual}%</span>
                    </div>
                    <div className={Style.totalItem}>
                        <label>Status:</label>
                        <span
                            className={Style.statusBadge}
                            style={{
                                backgroundColor:
                                    totais.status === 'Aprovado S/Res' ? '#28a74520' :
                                        totais.status === 'Aprovado C/Res' ? '#ffc10720' : '#dc354520',
                                color:
                                    totais.status === 'Aprovado S/Res' ? '#28a745' :
                                        totais.status === 'Aprovado C/Res' ? '#856404' : '#dc3545'
                            }}
                        >
                            {totais.status === 'Aprovado S/Res' && <IoCheckmarkCircle style={{ marginRight: 4 }} />}
                            {totais.status === 'Aprovado C/Res' && <IoInformationCircle style={{ marginRight: 4 }} />}
                            {totais.status === 'Reprovado' && <IoCloseCircle style={{ marginRight: 4 }} />}
                            {totais.status}
                        </span>
                    </div>
                </div>
            </div>
        );
    });

    const QuestionarioTab = React.memo(({ questionario, status }) => (
        <div className={Style.tabContent}>
            {questionario?.length ? (
                <div className={Style.questionarioContent}>
                    {questionario.map((grupo, index) => (
                        <div key={index} className={Style.perguntaGrupo}>
                            <div className={Style.titleGrupo}>
                                <h4>
                                    {grupo.titulo}
                                    <span
                                        className={Style.statusQuestionarioBadge}
                                        style={{
                                            backgroundColor: `${STATUS_MAP[status]?.color}20`,
                                            color: STATUS_MAP[status]?.color,
                                        }}
                                    >
                                        {STATUS_MAP[status]?.icon}
                                        {STATUS_MAP[status]?.label}
                                    </span>
                                </h4>
                            </div>

                            {
                                user.tipusu === "COMPRADOR" && grupo.titulo === "Controle e Gestão de Qualidade" && (
                                    <div>
                                        {questionario.some(grupo => grupo.titulo === "Controle e Gestão de Qualidade") && (
                                            <TotaisQualidade questionario={questionario} />
                                        )}
                                    </div>
                                )
                            }

                            {
                                grupo.perguntas.map((pergunta, i) => (
                                    <div key={i} className={Style.perguntaItem}>
                                        <label>{pergunta.pergunta}</label>
                                        <span>
                                            {pergunta.resposta || "-"}
                                            {grupo.titulo === "Controle e Gestão de Qualidade" && pergunta.resposta && pergunta.resposta !== '-' && pergunta.resposta !== 'N/A' && (
                                                ' ponto(s)'
                                            )}
                                        </span>
                                    </div>
                                ))
                            }
                        </div>
                    ))}
                    {
                        user.tipusu === "FORNECEDOR" && user.status === "QUESTIONARIO_PENDENTE" && (
                            <button
                                className={Style.resActionBtn}
                                onClick={() => {
                                    navigate("/cadastro");
                                }}
                            >
                                <IoSendOutline />
                                Responder Questionário
                            </button>
                        )
                    }

                </div>
            ) : (
                <div className={Style.emptyState}>
                    <FaCommentDots className={Style.emptyIcon} />
                    <h3>Nenhum questionário encontrado.</h3>
                </div>
            )}
        </div>
    ));

    const HistoricoTab = React.memo(({ historico }) => (
        <div className={Style.tabContent}>
            {historico?.length ? (
                <div className={Style.historicoList}>
                    {historico.map((item) => (
                        <div key={item.id} className={Style.historicoItem}>
                            <div className={Style.historicoHeader}>
                                <div className={Style.historicoInfo}>
                                    <span className={Style.historicoUsuario}>{item.usuario}</span>
                                    <span className={Style.historicoData}>{item.data}</span>
                                    <span className={Style.historicoTipo}>
                                        {item.tipoAprovacao === "CI" ? "Avaliação Fiscal" : "Avaliação Final"}
                                    </span>
                                </div>
                                <span
                                    className={Style.historicoStatus}
                                    style={{
                                        backgroundColor: `${STATUS_MAP[item.acao]?.color}20`,
                                        color: STATUS_MAP[item.acao]?.color
                                    }}
                                >
                                    {STATUS_MAP[item.acao]?.icon}
                                    {STATUS_MAP[item.acao]?.label}
                                </span>
                            </div>

                            {item.observacao && (
                                <div className={Style.historicoObservacao}>
                                    {item.observacao}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            ) : (
                <div className={Style.emptyState}>
                    <IoTimeOutline className={Style.emptyIcon} />
                    <h3>Nenhum histórico de aprovações encontrado.</h3>
                </div>
            )}
        </div>
    ));

    const AcaoButtons = React.memo(({ avaliacaoId, aprovacoes }) => {
        const getStatusInfo = useCallback((status) => {
            switch (status) {
                case "1": return { text: "Aprovado", color: "#28a745", icon: <IoCheckmarkCircle /> };
                case "0": return { text: "Reprovado", color: "#dc3545", icon: <IoCloseCircle /> };
                default: return { text: "Pendente", color: "#ffc107", icon: <IoTimeOutline /> };
            }
        }, []);

        const statusInicial = aprovacoes
            .filter(aprov => aprov.tipapr === "CI")
            .slice(0)[0]?.statusapr;

        const statusFinal = aprovacoes
            .filter(aprov => aprov.tipapr === "CF")
            .slice(0)[0]?.statusapr;

        const statusInicialInfo = getStatusInfo(statusInicial);
        const statusFinalInfo = getStatusInfo(statusFinal);
        const temAprovacaoInicial = aprovacoes.some(aprov => aprov.tipapr === "CI");
        const temAprovacaoFinal = aprovacoes.some(aprov => aprov.tipapr === "CF");

        return (
            <div className={Style.acaoButtons}>
                <div className={Style.acaoSection}>
                    <h3 className={Style.acaoSectionTitle}><IoDocumentTextOutline style={{ marginRight: '8px' }} /> Avaliação</h3>
                    <p className={Style.acaoSectionDescription}>
                        Aprove ou reprove o fornecedor neste registro de avaliação
                    </p>

                    <div className={Style.acaoGrupos}>
                        <div className={Style.acaoGrupo}>
                            <div className={Style.acaoGrupoHeader}>
                                <h5 className={Style.acaoGrupoTitle}>
                                    Avaliação Fiscal
                                </h5>
                                <span
                                    className={Style.statusIndicator}
                                    style={{
                                        backgroundColor: `${statusInicialInfo.color}20`,
                                        color: statusInicialInfo.color
                                    }}
                                >
                                    {statusInicialInfo.icon}
                                    {statusInicialInfo.text}
                                </span>
                            </div>

                            <div className={Style.acaoBotoes}>
                                <button
                                    className={`${Style.aprovarBtn} ${statusInicial === "1" ? Style.active : ''}`}
                                    onClick={() => aprovarAvaliacao(avaliacaoId, "CI")}
                                >
                                    <IoCheckmarkCircle />
                                    <span>Aprovar</span>
                                    <span className={Style.badgeInfo}>CI</span>
                                </button>
                                <button
                                    className={`${Style.reprovarBtn} ${statusInicial === "0" ? Style.active : ''}`}
                                    onClick={() => reprovarAvaliacao(avaliacaoId, "CI")}
                                >
                                    <IoCloseCircle />
                                    <span>Reprovar</span>
                                    <span className={Style.badgeInfo}>CI</span>
                                </button>
                            </div>
                        </div>

                        <div className={Style.separador}></div>

                        <div className={Style.acaoGrupo}>
                            <div className={Style.acaoGrupoHeader}>
                                <h5 className={Style.acaoGrupoTitle}>
                                    Avaliação Final
                                </h5>
                                <span
                                    className={Style.statusIndicator}
                                    style={{
                                        backgroundColor: `${statusFinalInfo.color}20`,
                                        color: statusFinalInfo.color
                                    }}
                                >
                                    {statusFinalInfo.icon}
                                    {statusFinalInfo.text}
                                </span>
                            </div>

                            <div className={Style.acaoBotoes}>
                                <button
                                    className={`${Style.aprovarBtn} ${statusFinal === "1" ? Style.active : ''}`}
                                    onClick={() => aprovarAvaliacao(avaliacaoId, "CF")}
                                >
                                    <IoCheckmarkCircle />
                                    <span>Aprovar</span>
                                    <span className={Style.badgeInfo}>CF</span>
                                </button>
                                <button
                                    className={`${Style.reprovarBtn} ${statusFinal === "0" ? Style.active : ''}`}
                                    onClick={() => reprovarAvaliacao(avaliacaoId, "CF")}
                                >
                                    <IoCloseCircle />
                                    <span>Reprovar</span>
                                    <span className={Style.badgeInfo}>CF</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className={Style.statusGeral}>
                        <div className={Style.statusItem}>
                            <span className={Style.statusLabel}>Status Geral:</span>
                            <span className={Style.statusValue}>
                                {temAprovacaoInicial && temAprovacaoFinal ? "Avaliação Completa" :
                                    temAprovacaoInicial || temAprovacaoFinal ? "Avaliação Parcial" :
                                        "Aguardando Avaliação"}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        );
    });

    const DocumentosTab = React.memo(({ documentos, carregando }) => (
        <div className={Style.tabContent}>
            {carregando ? (
                <Loading text="Carregando documentos..." />
            ) : documentos?.length ? (
                <div className={Style.documentosList}>
                    {documentos.map((doc) => (
                        <div key={doc.id} className={Style.documentoItem}>
                            <div className={Style.documentInfo}>
                                <IoDocumentTextOutline className={Style.documentIcon} />
                                <span className={Style.documentName}>{doc.nome}</span>
                            </div>
                            <a
                                href={doc.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={Style.downloadButton}
                            >
                                <IoOpenOutline className={Style.downloadIcon} /> Abrir
                            </a>
                        </div>
                    ))}
                </div>
            ) : (
                <div className={Style.emptyState}>
                    <IoDocumentTextOutline className={Style.emptyIcon} />
                    <h3>Nenhum documento do fornecedor encontrado.</h3>
                </div>
            )}
        </div>
    ));

    const RegistroItem = React.memo(({ registro }) => {
        const podeCarregarDocumentos = expandedRegistro === registro.id && activeRegistroTab === "documentos";

        const {
            documentos,
            carregando,
            erro,
            recarregarDocumentos
        } = useDocumentos(
            fornecedor?.codcli,
            registro.id,
            podeCarregarDocumentos
        );

        return (
            <div className={Style.registroItem}>
                <div
                    className={Style.registroHeader}
                    onClick={() => toggleRegistroExpansion(registro.id)}
                >
                    <div className={Style.registroInfo}>
                        <div className={Style.registroExpandIcon}>
                            {expandedRegistro === registro.id ?
                                <IoChevronDownOutline /> :
                                <IoChevronForwardOutline />
                            }
                        </div>
                        <div className={Style.registroMainInfo}>
                            <h4>
                                {registro.tipo} - Registro {registro.id}
                            </h4>

                            <div className={Style.registroMeta}>
                                <span className={Style.registroMetaItem}>
                                    <IoCalendarOutline />
                                    {registro.data}
                                </span>
                                {
                                    user.tipusu === 'COMPRADOR' && (
                                        <span className={Style.registroMetaItem}>
                                            <IoPersonOutline />
                                            {registro.avaliador}
                                        </span>
                                    )
                                }
                            </div>
                        </div>
                    </div>
                    {
                        user.tipusu === "COMPRADOR" ? (
                            <div className={Style.registroStatus}>
                                <span
                                    className={Style.statusBadge}
                                    style={{
                                        backgroundColor: `${STATUS_MAP[registro.acao]?.color}20`,
                                        color: STATUS_MAP[registro.acao]?.color
                                    }}
                                >
                                    {STATUS_MAP[registro.acao]?.icon}
                                    {STATUS_MAP[registro.acao]?.label}
                                </span>
                            </div>
                        ) : user.tipusu === "FORNECEDOR" && user.status === "QUESTIONARIO_PENDENTE" ? (
                            <div className={Style.registroStatus}>
                                <span
                                    className={Style.statusBadge}
                                    style={{
                                        backgroundColor: `${STATUS_MAP['pendente']?.color}20`,
                                        color: STATUS_MAP['pendente']?.color
                                    }}
                                >
                                    {STATUS_MAP['pendente']?.icon}
                                    {STATUS_MAP['pendente']?.label}
                                </span>
                            </div>
                        ) : (
                            <div className={Style.registroStatus}>
                                <span
                                    className={Style.statusBadge}
                                    style={{
                                        backgroundColor: `${STATUS_MAP[registro.acao]?.color}20`,
                                        color: STATUS_MAP[registro.acao]?.color
                                    }}
                                >
                                    {STATUS_MAP[registro.acao]?.icon}
                                    {STATUS_MAP[registro.acao]?.label}
                                </span>
                            </div>
                        )
                    }

                </div>

                {expandedRegistro === registro.id && (
                    <div className={Style.registroContent}>
                        <div className={Style.registroTabs}>
                            {
                                user.tipusu === "COMPRADOR" && (
                                    <button
                                        className={`${Style.registroTab} ${activeRegistroTab === "avaliacao" ? Style.activeRegistroTab : ""}`}
                                        onClick={() => handleRegistroTabChange("avaliacao")}
                                    >
                                        <IoDocumentTextOutline style={{ marginRight: 8 }} />
                                        Avaliação
                                    </button>
                                )
                            }

                            <button
                                className={`${Style.registroTab} ${activeRegistroTab === "questionario" ? Style.activeRegistroTab : ""}`}
                                onClick={() => handleRegistroTabChange("questionario")}
                            >
                                <IoListOutline style={{ marginRight: 8 }} />
                                Questionário
                            </button>

                            <button
                                className={`${Style.registroTab} ${activeRegistroTab === "documentos" ? Style.activeRegistroTab : ""}`}
                                onClick={() => handleRegistroTabChange("documentos")}
                            >
                                <IoDocumentTextOutline style={{ marginRight: 8 }} />
                                Documentos
                                {carregando && <span className={Style.loadingDot}></span>}
                            </button>

                            {
                                user.tipusu === "COMPRADOR" && (
                                    <button
                                        className={`${Style.registroTab} ${activeRegistroTab === "historico" ? Style.activeRegistroTab : ""}`}
                                        onClick={() => handleRegistroTabChange("historico")}
                                    >
                                        <IoTimeOutline style={{ marginRight: 8 }} />
                                        Histórico
                                    </button>
                                )
                            }
                        </div>

                        <div className={Style.registroTabContent}>
                            {activeRegistroTab === "documentos" && (
                                <DocumentosTab
                                    documentos={documentos}
                                    carregando={carregando}
                                    erro={erro}
                                    onRecarregar={recarregarDocumentos}
                                />
                            )}
                            {activeRegistroTab === "questionario" && (
                                <QuestionarioTab questionario={registro.questionario} status={registro.status_questionario} />
                            )}
                            {activeRegistroTab === "historico" && (
                                <HistoricoTab historico={registro.historico} />
                            )}
                            {activeRegistroTab === "avaliacao" && (
                                <div className={Style.registroActions}>
                                    <AcaoButtons
                                        avaliacaoId={registro.id}
                                        aprovacoes={registro.aprovacoes}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    });

    const parseSintegraTexto = (texto = "") => {
        return texto
            .split("\n")
            .map(l => l.trim())
            .filter(l => l && l.includes(":"))
            .map(l => {
                const [label, ...rest] = l.split(":");
                return {
                    label: label.trim(),
                    value: rest.join(":").trim()
                };
            });
    };

    const SintegraTab = React.memo(() => {
        if (!consultaSintegra?.texsci) {
            return (
                <div className={Style.tabContent}>
                    <div className={Style.emptyState}>
                        <GrIntegration className={Style.emptyIcon} />
                        <h3>Nenhuma consulta ao Sintegra encontrada.</h3>
                    </div>
                </div>
            );
        }

        const dados = parseSintegraTexto(consultaSintegra.texsci);

        return (
            <div className={Style.tabContent}>
                <div className={Style.cardGroup}>
                    <h3>Consulta Sintegra</h3>

                    <div className={Style.cardsContainer}>
                        <div className={Style.card}>
                            <span className={Style.cardLabel}>Data da Consulta</span>
                            <span className={Style.cardValue}>{consultaSintegra.datsci}</span>
                        </div>

                        <div className={Style.card}>
                            <span className={Style.cardLabel}>Responsável</span>
                            <span className={Style.cardValue}>
                                {consultaSintegra.conpor} - {consultaSintegra.nompor}
                            </span>
                        </div>
                    </div>
                </div>

                <div className={Style.cardGroup}>
                    <h3>Dados do Sintegra</h3>
                    <div className={Style.cardsContainer}>
                        {dados.map((item, index) => (
                            <div key={index} className={Style.card}>
                                <span className={Style.cardLabel}>{item.label}</span>
                                <span className={Style.cardValue}>{item.value || "-"}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    });

    const RegistrosTab = React.memo(() => {
        const avaliacoesMapeadas = mapearAvaliacoesDaAPI(avaliacoes);

        if (isLoadingAvaliacoes) {
            return (
                <div className={Style.tabContent}>
                    <Loading text={"Carregando avaliações..."} />
                </div>
            );
        }

        return (
            <div className={Style.tabContent}>
                <div className={Style.registrosHeader}>
                    <div className={Style.registrosTitle}>
                        {
                            user.tipusu === "COMPRADOR" ? (
                                <>
                                    <h3><IoFolderOpenOutline style={{ marginRight: '8px' }} /> Registros de Avaliação</h3>
                                    <p>Histórico completo de avaliações e aprovações do fornecedor</p>
                                </>
                            ) : (
                                <>
                                    <h3><FaCommentDots style={{ marginRight: '8px' }} /> Questionário</h3>
                                    <p>Questionário respondido pelo fornecedor</p>
                                </>
                            )
                        }
                    </div>
                    <br />
                    {
                        user.tipusu === "COMPRADOR" && (
                            <button
                                className={Style.primaryActionBtn}
                                onClick={novaAvaliacao}
                            >
                                <IoAddCircleOutline />
                                Nova Avaliação
                            </button>
                        )
                    }
                </div>

                <div className={Style.registrosList}>
                    {avaliacoesMapeadas.length > 0 ? (
                        avaliacoesMapeadas.map((registro) => (
                            <RegistroItem
                                key={registro.id}
                                registro={registro}
                            />
                        ))
                    ) : (
                        <div className={Style.emptyState}>
                            <IoDocumentTextOutline className={Style.emptyIcon} />
                            <h3>Nenhum registro de avaliação encontrado.</h3>
                        </div>
                    )}
                </div>
            </div>
        );
    });

    if (isLoadingFornecedor) {
        return <Loading text={"Carregando fornecedor..."} />;
    }

    if (!fornecedor) {
        return (
            <div className="empty">
                <div className={`${Style.emptyState} ${Style.noBackground}`}>
                    <FaUser className={Style.emptyIcon} />
                    <h3>Fornecedor não encontrado</h3>
                </div>
            </div>
        )
    }

    return (
        <div className={Style.container}>
            {showEmailModal && (
                <EmailModal
                    show={showEmailModal}
                    fornecedor={fornecedor}
                    emailData={emailData}
                    setEmailData={setEmailData}
                    onClose={() => {
                        setShowEmailModal(false);
                        setEmailData({ destinatario: "", assunto: "", conteudo: "" });
                    }}
                    onSend={handleEnviarEmail}
                    isLoading={isLoadingAction}
                />
            )}

            {isLoadingAction && (
                <Loading text="Carregando fornecedor..." />
            )}

            <div className={Style.header}>
                {user.tipusu === "COMPRADOR" && (
                    <>
                        <button className={Style.backButton} onClick={() => navigate(-1)}>
                            <IoArrowBackCircle className={Style.backIcon} />
                        </button>
                        <h2>Fornecedor {fornecedor.nomcli?.slice(0, 12)}.</h2>
                    </>
                )}
            </div>

            {user.tipusu === "COMPRADOR" && (
                <div className={Style.actionsDropdown}>
                    <button className={Style.actionsButton} onClick={toggleDropdown}>
                        Ações ▾
                    </button>
                    {open && (
                        <div className={Style.dropdownContent}>
                            <div className={Style.submenu}>
                                <button onClick={() => setOpenSubAfastar(!openSubAfastar)}>
                                    <MdBlock style={{ marginRight: 8 }} /> Afastar ▸
                                </button>
                                {openSubAfastar && (
                                    <div className={Style.submenuContent}>
                                        <button onClick={ativarFornecedor}>Ativar</button>
                                        <button onClick={inativarFornecedor}>Inativar</button>
                                    </div>
                                )}
                            </div>

                            <button onClick={gerarSenhaTemporaria}>
                                <IoKeyOutline style={{ marginRight: 8 }} /> Senha Temp.
                            </button>

                            <button onClick={handleEnviarEmailClick}>
                                <IoMailOutline style={{ marginRight: 8 }} /> Enviar Email
                            </button>
                        </div>
                    )}
                </div>
            )}

            {user.tipusu === "COMPRADOR" && (
                <div className={Style.tabsContainer}>
                    <button
                        className={`${Style.tabButton} ${activeTab === "dados" ? Style.activeTab : ""}`}
                        onClick={() => setActiveTab("dados")}
                    >
                        <IoInformationCircle style={{ marginRight: 8 }} />
                        Dados Cadastrais
                    </button>
                    <button
                        className={`${Style.tabButton} ${activeTab === "sintegra" ? Style.activeTab : ""}`}
                        onClick={() => setActiveTab("sintegra")}
                    >
                        <GrIntegration style={{ marginRight: 8 }} />
                        Sintegra
                    </button>
                    <button
                        className={`${Style.tabButton} ${activeTab === "registros" ? Style.activeTab : ""}`}
                        onClick={() => setActiveTab("registros")}
                    >
                        <IoFolderOpenOutline style={{ marginRight: 8 }} />
                        Registros
                    </button>
                </div>
            )}

            {user.tipusu === "FORNECEDOR" && (
                <div className={Style.tabsContainer}>
                    <button
                        className={`${Style.tabButton} ${activeTab === "dados" ? Style.activeTab : ""}`}
                        onClick={() => setActiveTab("dados")}
                    >
                        <IoInformationCircle style={{ marginRight: 8 }} />
                        Dados Cadastrais
                    </button>
                    <button
                        className={`${Style.tabButton} ${activeTab === "registros" ? Style.activeTab : ""}`}
                        onClick={() => setActiveTab("registros")}
                    >
                        <IoFolderOpenOutline style={{ marginRight: 8 }} />
                        Questionário
                    </button>
                </div>
            )}

            <div className={Style.tabContentContainer}>
                {activeTab === "dados" && (
                    <>
                        {renderCardGroup("Cadastro", FIELD_GROUPS.dadosBasicos)}
                        {renderCardGroup("Endereço", FIELD_GROUPS.endereco)}
                        {renderCardGroup("Contatos", FIELD_GROUPS.contatos)}
                        {user.tipusu === "COMPRADOR" && (
                            renderCardGroup("Status", FIELD_GROUPS.status, true)
                        )}
                    </>
                )}
                {activeTab === "registros" && <RegistrosTab />}
                {activeTab === "sintegra" && <SintegraTab />}
            </div>
        </div>
    );
};